import { environment } from 'src/environments/environment';
import { InjectionToken, Signal } from '@angular/core';
import type { ContractActivity, ContractActionType, ActivityDetails } from '@shared/definitions/contract-activity';



/**
 * Pattern B contract for Contract Activities (audit log of contract events).
 *
 * Contract-scoped: call `loadForContract(contractUID)` first. Writes are
 * cloud-function-backed (the legacy service calls `httpsCallable`).
 */
export interface IContractActivitiesStore {
  readonly contractUID: Signal<string | null>;
  readonly activities: Signal<readonly ContractActivity[]>;
  readonly loading: Signal<boolean>;
  readonly lastError: Signal<Error | null>;

  readonly totalCount: Signal<number>;

  loadForContract(contractUID: string): Promise<void>;
  refresh(): Promise<void>;

  logActivity(
    contractUID: string,
    action: ContractActionType,
    description: string,
    details?: ActivityDetails,
    actionDate?: Date
  ): Promise<string>;

  logIndexationNotification(
    contractUID: string,
    indexationType: string,
    baseIndexValue: number,
    newIndexValue: number,
    previousRentAmount: number,
    newRentAmount: number,
    effectiveDate: Date,
    indexationCap?: number,
    notes?: string
  ): Promise<string>;

  logIndexationApplied(
    contractUID: string,
    indexationType: string,
    indexationRate: number,
    previousRentAmount: number,
    newRentAmount: number,
    effectiveDate: Date
  ): Promise<string>;

  logTerminationNotification(
    contractUID: string,
    terminationDate: Date,
    reason?: string,
    noticeGiven?: Date
  ): Promise<string>;

  getActivityStats(): Readonly<Record<string, number>>;
  getActivityCount(): number;
}

export const CONTRACT_ACTIVITIES_STORE = new InjectionToken<IContractActivitiesStore>(
  'CONTRACT_ACTIVITIES_STORE');

export type ContractActivitiesStoreMode = 'mock' | 'firestore';

export function resolveContractActivitiesStoreMode(): ContractActivitiesStoreMode {
  if (typeof window === 'undefined') return 'firestore';
  if (environment.production) return 'firestore'; // QA-only mock store: never honour ?store=mock in production (silent data loss).
  const url = new URL(window.location.href);
  const param = url.searchParams.get('store');
  if (param === 'firestore' || param === 'mock') {
    try {
      window.localStorage.setItem('contractActivities.storeMode', param);
    } catch {
      /* ignore */
    }
    return param;
  }
  try {
    const stored = window.localStorage.getItem('contractActivities.storeMode');
    if (stored === 'firestore' || stored === 'mock') return stored;
  } catch {
    /* ignore */
  }
  return 'firestore';
}
